import React, { useState } from 'react';
import './NetworkBreakdown.css';

interface NetworkRequest {
  url: string;
  encodedDataLength: number;
  timing: number;
  isInitialWindow: boolean;
  status?: number;
  protocol?: string;
  encoding?: string;
  mimeType?: string;
}

interface NetworkBreakdownProps {
  requests?: NetworkRequest[];
}

const getStatusColor = (status?: number) => {
  if (!status) return '#999';
  if (status >= 200 && status < 300) return '#4CAF50';
  if (status >= 300 && status < 400) return '#FF9800';
  return '#f44336';
};

const isHttps = (url: string) => url.startsWith('https://');

export const NetworkBreakdown: React.FC<NetworkBreakdownProps> = ({ requests = [] }) => {
  const [expanded, setExpanded] = useState(false);

  if (!requests || requests.length === 0) return null;

  const totalSize = requests.reduce((sum, r) => sum + r.encodedDataLength, 0);
  const maxSize = Math.max(...requests.map(r => r.encodedDataLength));

  return (
    <div className="network-breakdown">
      <div className="network-header" onClick={() => setExpanded(!expanded)}>
        <h3>Network Requests ({requests.length})</h3>
        <div className="network-stats">
          <span>Total: {(totalSize / 1024).toFixed(2)} KB</span>
          <span className="expand-icon">{expanded ? '▼' : '▶'}</span>
        </div>
      </div>
      {expanded && (
        <table className="network-table">
          <thead>
            <tr>
              <th>URL</th>
              <th>Status</th>
              <th>Protocol</th>
              <th>Encoding</th>
              <th>Timing</th>
              <th>Size</th>
              <th style={{ width: '150px' }}>Relative Size</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((req, idx) => {
              const percentage = (req.encodedDataLength / maxSize) * 100;
              const hostname = new URL(req.url).hostname;
              
              return (
                <tr key={idx}>
                  <td className="network-url">
                    {isHttps(req.url) ? (
                      <span className="https-lock">🔒</span>
                    ) : (
                      <span className="http-warning">⚠️</span>
                    )}
                    <span className="url-text" title={req.url}>{hostname}</span>
                  </td>
                  <td 
                    className="network-status"
                    style={{ color: getStatusColor(req.status) }}
                  >
                    {req.status || '-'}
                  </td>
                  <td className="network-protocol">
                    <span className="protocol-badge">{req.protocol || '-'}</span>
                  </td>
                  <td className="network-encoding">
                    {req.encoding || '-'}
                  </td>
                  <td className="network-timing">
                    {req.timing.toFixed(3)}s
                  </td>
                  <td className="network-size">
                    {(req.encodedDataLength / 1024).toFixed(2)} KB
                  </td>
                  <td className="network-bar">
                    <div 
                      className="size-bar" 
                      style={{ width: `${percentage}%` }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};